import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, CalendarCheck, CheckCheck, ChevronLeft, ChevronRight, Eraser, Lock, MoreHorizontal, Printer, Unlock } from 'lucide-react';
import { Button, Modal, Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import {
  type PointageCellule,
  type PointageLigneEmploye,
  type PointageType,
  cloturerPointages,
  decloturerPointages,
  deletePointageJour,
  getPointageMensuel,
  openFeuillePointage,
  remplirJour,
  remplirMois,
  upsertPointageJour,
} from '@/services/rh';
import { listChantiers } from '@/services/chantiers';
import { useAuthStore } from '@/store/authStore';
import { toast } from '@/store/toastStore';

/* --------------------------- Constantes & palette -------------------------- */
const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const JOUR_LETTRE = ['D', 'L', 'M', 'M', 'J', 'V', 'S']; // getDay() 0=Dim..6=Sam
const JOUR_ABBR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const JOUR_NOM = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

const TYPE_UI: Record<PointageType, { label: string; abbr: string; color: string }> = {
  PRESENT: { label: 'Présent', abbr: 'P', color: '#16a34a' },
  ABSENT_NON_JUSTIFIE: { label: 'Absent', abbr: 'A', color: '#dc2626' },
  ABSENT_JUSTIFIE: { label: 'Absent justifié', abbr: 'AJ', color: '#b45309' },
  PERMISSION: { label: 'Permission', abbr: 'Pe', color: '#ea580c' },
  CONGE: { label: 'Congé', abbr: 'C', color: '#2563eb' },
  MALADIE: { label: 'Maladie', abbr: 'M', color: '#9333ea' },
  MISSION: { label: 'Mission', abbr: 'Mi', color: '#f97316' },
  DEPLACEMENT: { label: 'Déplacement', abbr: 'D', color: '#0891b2' },
  FERIE: { label: 'Férié', abbr: 'F', color: '#64748b' },
  DEMI_MATIN: { label: 'Demi (matin)', abbr: '½m', color: '#22c55e' },
  DEMI_APREM: { label: 'Demi (après-midi)', abbr: '½s', color: '#15803d' },
  HORAIRE_LIBRE: { label: 'Horaire libre', abbr: 'H', color: '#eab308' },
};
// Ordre du menu de sélection (rapides d'abord). La demi-journée a été retirée.
const TYPES_ORDRE: PointageType[] = [
  'PRESENT', 'ABSENT_NON_JUSTIFIE', 'ABSENT_JUSTIFIE', 'PERMISSION', 'CONGE', 'MALADIE',
  'MISSION', 'DEPLACEMENT', 'HORAIRE_LIBRE', 'FERIE',
];
const TYPES_AVEC_HEURES: PointageType[] = ['PRESENT', 'MISSION', 'DEPLACEMENT', 'HORAIRE_LIBRE'];
const TYPES_TRAVAIL: PointageType[] = ['PRESENT', 'MISSION', 'DEPLACEMENT', 'HORAIRE_LIBRE'];
// Statuts qui demandent une précision de lieu / objet (saisie d'un commentaire).
const TYPES_AVEC_LIEU: PointageType[] = ['MISSION', 'DEPLACEMENT'];

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
/** Samedi qui ouvre la semaine contenant `d` (la semaine va du samedi au vendredi). */
const samediSemaine = (d: Date) => addDays(d, -(((d.getDay() - 6) + 7) % 7));
/** Vendredi = jour de repos hebdomadaire. */
const estRepos = (d: Date) => d.getDay() === 5;
const hm = (t: string | null | undefined) => (t ? t.slice(0, 5) : '');
const heuresEntre = (a: string, b: string): number => {
  if (!a || !b) return 0;
  const [ah, am] = a.split(':').map(Number); const [bh, bm] = b.split(':').map(Number);
  let m = (bh * 60 + bm) - (ah * 60 + am); if (m < 0) m += 1440; return m / 60;
};
function heuresDefaut(type: PointageType): number {
  if (type === 'PRESENT' || type === 'MISSION' || type === 'DEPLACEMENT') return 8;
  return 0;
}
const round1 = (n: number) => Math.round(n * 10) / 10;

type Vue = 'jour' | 'semaine' | 'mois';
type Over = Record<string, PointageType | 'CLEAR'>;

/* ------------------------------ Composant racine --------------------------- */
export function PointagesTab() {
  const queryClient = useQueryClient();
  const [vue, setVue] = useState<Vue>('jour');
  const [cursor, setCursor] = useState(() => new Date());
  const [over, setOver] = useState<Over>({});
  const [menu, setMenu] = useState<{ key: string; date: string; employeId: string; x: number; y: number } | null>(null);
  const [impression, setImpression] = useState(false);
  const [confirmFill, setConfirmFill] = useState<{ scope: 'jour' | 'mois' | 'colonne'; date?: string; label: string; n: number } | null>(null);
  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN'); // Axe 7 — clôture/modif verrouillé réservés ADMIN

  // Impression = vrai PDF serveur « Feuille de présence », reflétant la VUE active
  // (journalière = liste, hebdo = portrait, mensuel = paysage). Axe 6.
  const imprimerFeuille = async () => {
    setImpression(true);
    try {
      await openFeuillePointage(vue, iso(cursor));
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setImpression(false);
    }
  };

  const { data: chantiersData } = useQuery({ queryKey: ['chantiers'], queryFn: () => listChantiers() });
  const chantiers = chantiersData?.items ?? [];

  // Dates visibles selon la vue.
  const visibleDates = useMemo<Date[]>(() => {
    if (vue === 'jour') return [cursor];
    if (vue === 'semaine') { const s = samediSemaine(cursor); return Array.from({ length: 7 }, (_, i) => addDays(s, i)); }
    const y = cursor.getFullYear(), m = cursor.getMonth(), n = new Date(y, m + 1, 0).getDate();
    return Array.from({ length: n }, (_, i) => new Date(y, m, i + 1));
  }, [vue, cursor]);

  // Mois à charger (1 ou 2 si la semaine chevauche deux mois).
  const d0 = visibleDates[0]; const dN = visibleDates[visibleDates.length - 1];
  const p0 = { a: d0.getFullYear(), m: d0.getMonth() + 1 };
  const p1 = { a: dN.getFullYear(), m: dN.getMonth() + 1 };
  const memeMois = p0.a === p1.a && p0.m === p1.m;
  const q0 = useQuery({ queryKey: ['pointage-mensuel', p0.a, p0.m], queryFn: () => getPointageMensuel(p0.m, p0.a) });
  const q1 = useQuery({ queryKey: ['pointage-mensuel', p1.a, p1.m], queryFn: () => getPointageMensuel(p1.m, p1.a), enabled: !memeMois });

  // Index fusionné : employés + cellules par `${employeId}|${iso}`.
  const { employes, cells } = useMemo(() => {
    const emps = new Map<string, PointageLigneEmploye>();
    const cs = new Map<string, PointageCellule>();
    for (const [resp, per] of [[q0.data, p0], [memeMois ? null : q1.data, p1]] as const) {
      if (!resp) continue;
      for (const l of resp.lignes) {
        if (!emps.has(l.employe_id)) emps.set(l.employe_id, l);
        for (const [d, c] of Object.entries(l.jours)) {
          cs.set(`${l.employe_id}|${per.a}-${pad(per.m)}-${pad(Number(d))}`, c);
        }
      }
    }
    return { employes: [...emps.values()], cells: cs };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q0.data, q1.data, memeMois]);

  // Re-synchronise l'overlay quand les données serveur changent (après refetch).
  useEffect(() => { setOver({}); }, [q0.data, q1.data]);

  const loading = q0.isLoading || (!memeMois && q1.isLoading);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['pointage-mensuel'] });

  /* ------------------------------ Mutations ------------------------------ */
  const upsertM = useMutation({
    mutationFn: (v: Parameters<typeof upsertPointageJour>[0]) => upsertPointageJour(v),
    onError: (e) => { toast.error(getApiErrorMessage(e)); invalidate(); },
  });
  const deleteM = useMutation({
    mutationFn: (v: { employe_id: string; date: string }) => deletePointageJour(v.employe_id, v.date),
    onError: (e) => { toast.error(getApiErrorMessage(e)); invalidate(); },
  });

  const typeOf = (employeId: string, date: string): PointageType | null => {
    const o = over[`${employeId}|${date}`];
    if (o !== undefined) return o === 'CLEAR' ? null : o;
    return cells.get(`${employeId}|${date}`)?.type ?? null;
  };
  const heuresOf = (employeId: string, date: string): number => {
    const key = `${employeId}|${date}`;
    const t = typeOf(employeId, date);
    if (!t) return 0;
    const c = cells.get(key);
    const o = over[key];
    if (o !== undefined && o !== 'CLEAR') return c && c.type === o ? Number(c.heures_normales) + Number(c.heures_sup_25) + Number(c.heures_sup_50) : heuresDefaut(o);
    return c ? Number(c.heures_normales) + Number(c.heures_sup_25) + Number(c.heures_sup_50) : 0;
  };

  // Commentaire enregistré (lieu de mission, etc.) — depuis les données serveur.
  const commentOf = (employeId: string, date: string): string | null => cells.get(`${employeId}|${date}`)?.commentaire ?? null;
  // Plage horaire « HH:MM–HH:MM » (bulle d'info, ex. horaire libre 10:00–14:00).
  const plageOf = (employeId: string, date: string): string | null => {
    const c = cells.get(`${employeId}|${date}`);
    return c?.heure_arrivee && c?.heure_depart ? `${hm(c.heure_arrivee)}–${hm(c.heure_depart)}` : null;
  };
  // Axe 7 — la cellule est-elle clôturée (verrouillée) ?
  const verrouOf = (employeId: string, date: string): boolean => cells.get(`${employeId}|${date}`)?.verrouille ?? false;
  // Gate de saisie : refuse une cellule clôturée (sauf ADMIN, après confirmation tracée).
  const tenterSaisie = (employeId: string, date: string): boolean => {
    if (!verrouOf(employeId, date)) return true;
    if (!isAdmin) { toast.error('Période clôturée — modification réservée à l\'administrateur.'); return false; }
    return window.confirm('Modifier un pointage clôturé — cette action est tracée. Continuer ?');
  };

  const setCell = (employeId: string, date: string, type: PointageType | null) => {
    const key = `${employeId}|${date}`;
    setOver((p) => ({ ...p, [key]: type === null ? 'CLEAR' : type }));
    if (type === null) deleteM.mutate({ employe_id: employeId, date });
    else upsertM.mutate({ employe_id: employeId, date, type });
  };

  // Stats d'une plage de dates pour un employé (heures + jours travaillés).
  const stats = (employeId: string, dates: Date[]) => {
    let h = 0, j = 0;
    for (const d of dates) {
      const k = iso(d); const t = typeOf(employeId, k);
      if (!t) continue;
      h += heuresOf(employeId, k);
      if (TYPES_TRAVAIL.includes(t)) j += 1;
    }
    return { h: round1(h), j };
  };

  /* --------------------------- Remplissage masse ------------------------- */
  const remplirJourM = useMutation({
    mutationFn: (v: { date: string; type: PointageType }) => remplirJour(v.date, v.type),
    onSuccess: (r) => { invalidate(); toast.success(r.detail); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const remplirMoisM = useMutation({
    mutationFn: () => remplirMois(cursor.getFullYear(), cursor.getMonth() + 1, 'PRESENT', true),
    onSuccess: (r) => { invalidate(); toast.success(r.detail); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  // « tout » / « effacer » une plage pour un employé.
  const setRange = (employeId: string, dates: Date[], type: PointageType | null) => {
    dates.forEach((d) => { if (type === 'PRESENT' && estRepos(d)) return; setCell(employeId, iso(d), type); });
  };

  // Axe 5 — remplissage sécurisé : ne touche QUE les cases vides, après confirmation.
  const countVides = (dates: Date[], excludeRepos: boolean) => {
    let n = 0;
    for (const e of employes) for (const d of dates) { if (excludeRepos && estRepos(d)) continue; if (typeOf(e.employe_id, iso(d)) === null) n += 1; }
    return n;
  };
  const demanderRemplir = (scope: 'jour' | 'mois' | 'colonne', date?: string) => {
    if (scope === 'mois') { setConfirmFill({ scope, label: `${MOIS[cursor.getMonth()]} ${cursor.getFullYear()}`, n: countVides(visibleDates, true) }); return; }
    const d = scope === 'colonne' && date ? new Date(`${date}T00:00:00`) : cursor;
    const isoD = scope === 'colonne' && date ? date : iso(cursor);
    setConfirmFill({ scope, date: isoD, label: `le ${JOUR_NOM[d.getDay()]} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}`, n: countVides([d], false) });
  };
  const executerRemplir = () => {
    if (!confirmFill || confirmFill.n === 0) { setConfirmFill(null); return; }
    if (confirmFill.scope === 'mois') remplirMoisM.mutate();
    else remplirJourM.mutate({ date: confirmFill.date!, type: 'PRESENT' });
    setConfirmFill(null);
  };

  // Axe 7 — clôture / déclôture de la période affichée (ADMIN ; vérifié serveur).
  const cloturerM = useMutation({
    mutationFn: () => cloturerPointages(vue, iso(cursor)),
    onSuccess: (r) => { invalidate(); toast.success(r.detail); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const decloturerM = useMutation({
    mutationFn: () => decloturerPointages(vue, iso(cursor)),
    onSuccess: (r) => { invalidate(); toast.success(r.detail); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const periodeLabel = vue === 'jour' ? 'la journée' : vue === 'semaine' ? 'la semaine' : 'le mois';

  /* ---------------------------- Navigation ------------------------------- */
  const navMois = (delta: number) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  const navJour = (delta: number) => setCursor((c) => addDays(c, delta));
  const navSemaine = (delta: number) => setCursor((c) => addDays(c, delta * 7));

  // Axe 4 — pouls de la période affichée (calculé depuis les données réelles).
  const pouls = (() => {
    const nb = employes.length;
    const ouvres = visibleDates.filter((d) => !estRepos(d)).length;
    if (vue === 'jour') {
      const k = iso(cursor);
      let pres = 0, abs = 0, vide = 0;
      for (const e of employes) {
        const t = typeOf(e.employe_id, k);
        if (t === null) vide += 1;
        else if (t === 'ABSENT_NON_JUSTIFIE') abs += 1;
        else if (TYPES_TRAVAIL.includes(t)) pres += 1;
      }
      return `${pres} présent${pres > 1 ? 's' : ''} · ${abs} absent${abs > 1 ? 's' : ''} · ${vide} à pointer`;
    }
    let absences = 0, hl = 0, totalH = 0;
    for (const e of employes) for (const d of visibleDates) {
      const t = typeOf(e.employe_id, iso(d));
      if (!t) continue;
      if (t === 'ABSENT_NON_JUSTIFIE') absences += 1;
      if (t === 'HORAIRE_LIBRE') hl += 1;
      totalH += heuresOf(e.employe_id, iso(d));
    }
    const sfx = `${absences} absence${absences > 1 ? 's' : ''} · ${hl} horaire${hl > 1 ? 's' : ''} libre${hl > 1 ? 's' : ''}`;
    if (vue === 'semaine') return `${nb} employés · ${ouvres} j ouvrés · ${sfx}`;
    return `${nb} employés · ${ouvres} j ouvrés · ${round1(totalH)} h / ${nb * ouvres * 8} h · ${sfx}`;
  })();

  return (
    <div className="space-y-4" onClick={() => menu && setMenu(null)}>
      {/* Barre d'outils */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface-secondary p-3 shadow-sm">
        <div className="inline-flex rounded-md border border-border p-0.5">
          {(['jour', 'semaine', 'mois'] as Vue[]).map((v) => (
            <button key={v} onClick={() => setVue(v)} className={cn('rounded px-3 py-1.5 text-sm font-medium capitalize transition-colors', vue === v ? 'bg-brand-accent text-brand-accent-contrast' : 'text-content-secondary hover:bg-surface-hover')}>
              {v === 'jour' ? 'Journalier' : v === 'semaine' ? 'Hebdomadaire' : 'Mensuel'}
            </button>
          ))}
        </div>

        {vue === 'mois' && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => navMois(-1)} aria-label="Mois précédent"><ChevronLeft className="size-4" /></Button>
            <span className="min-w-[140px] text-center text-sm font-semibold text-content-primary">{MOIS[cursor.getMonth()]} {cursor.getFullYear()}</span>
            <Button variant="secondary" size="sm" onClick={() => navMois(1)} aria-label="Mois suivant"><ChevronRight className="size-4" /></Button>
            <Button variant="secondary" size="sm" onClick={() => setCursor(new Date())}>Ce mois-ci</Button>
          </div>
        )}
        {vue === 'semaine' && (() => {
          const sat = samediSemaine(cursor); const fri = addDays(sat, 6);
          return (
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => navSemaine(-1)}><ChevronLeft className="size-4" /></Button>
              <span className="min-w-[180px] text-center text-sm font-semibold text-content-primary">Sem. {pad(sat.getDate())}/{pad(sat.getMonth() + 1)} → {pad(fri.getDate())}/{pad(fri.getMonth() + 1)}</span>
              <Button variant="secondary" size="sm" onClick={() => navSemaine(1)}><ChevronRight className="size-4" /></Button>
              <Button variant="secondary" size="sm" onClick={() => setCursor(new Date())}>Cette semaine</Button>
            </div>
          );
        })()}
        {vue === 'jour' && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => navJour(-1)}><ChevronLeft className="size-4" /></Button>
            <span className="min-w-[210px] text-center text-sm font-semibold text-content-primary">{JOUR_NOM[cursor.getDay()]} {pad(cursor.getDate())}/{pad(cursor.getMonth() + 1)}/{cursor.getFullYear()} {estRepos(cursor) ? '· repos' : ''}</span>
            <Button variant="secondary" size="sm" onClick={() => navJour(1)}><ChevronRight className="size-4" /></Button>
            <Button variant="secondary" size="sm" onClick={() => setCursor(new Date())}>Aujourd'hui</Button>
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          {vue === 'jour' && <Button size="sm" onClick={() => demanderRemplir('jour')} loading={remplirJourM.isPending}><CheckCheck className="size-4" /> Marquer présents (cases vides)</Button>}
          {vue === 'mois' && <Button size="sm" onClick={() => demanderRemplir('mois')} loading={remplirMoisM.isPending}><CheckCheck className="size-4" /> Marquer présents (cases vides)</Button>}
          <Button variant="secondary" size="sm" onClick={imprimerFeuille} loading={impression} title="Imprimer la feuille de présence (PDF)"><Printer className="size-4" /> Imprimer</Button>
          {isAdmin && (
            <>
              <Button variant="secondary" size="sm" onClick={() => { if (window.confirm(`Clôturer ${periodeLabel} ? Les pointages passeront en lecture seule (modifiables uniquement par l'administrateur, avec traçage).`)) cloturerM.mutate(); }} loading={cloturerM.isPending} title="Verrouiller la période (base de paie figée)"><Lock className="size-4" /> Clôturer</Button>
              <Button variant="ghost" size="sm" onClick={() => { if (window.confirm(`Déclôturer ${periodeLabel} ?`)) decloturerM.mutate(); }} loading={decloturerM.isPending} title="Déverrouiller la période"><Unlock className="size-4" /> Déclôturer</Button>
            </>
          )}
        </div>
      </div>

      {/* Axe 4 — pouls de la période */}
      <div className="flex items-center gap-1.5 text-sm text-content-secondary">
        <Activity className="size-4 shrink-0 text-content-muted" />
        <span>{pouls}</span>
      </div>

      <p className="text-xs text-content-muted">Semaine du samedi au jeudi · vendredi = repos · horaire standard 08:00–16:00 (8 h). Cliquez une case pour choisir le statut. En vue <strong>journalière</strong> : précisez le <strong>lieu</strong> pour une mission / un déplacement et les <strong>heures</strong> pour un horaire libre.</p>

      {loading ? (
        <Skeleton className="h-72 w-full" />
      ) : employes.length === 0 ? (
        <p className="py-16 text-center text-sm text-content-muted">Aucun employé actif. Ajoutez des employés pour saisir leur pointage.</p>
      ) : vue === 'jour' ? (
        <VueJournaliere employes={employes} date={cursor} cells={cells} chantiers={chantiers} typeOf={typeOf}
          onSave={(p) => { if (!tenterSaisie(p.employe_id, p.date)) return; setOver((o) => ({ ...o, [`${p.employe_id}|${p.date}`]: p.type })); upsertM.mutate(p, { onSuccess: () => invalidate() }); }}
          onClear={(employeId) => { if (!tenterSaisie(employeId, iso(cursor))) return; setCell(employeId, iso(cursor), null); }}
          onMenu={(employeId, e) => { if (!tenterSaisie(employeId, iso(cursor))) return; setMenu({ key: `${employeId}|${iso(cursor)}`, employeId, date: iso(cursor), x: e.clientX, y: e.clientY }); }} />
      ) : (
        <Grille
          vue={vue} dates={visibleDates} employes={employes} typeOf={typeOf} heuresOf={heuresOf} commentOf={commentOf} plageOf={plageOf} verrouOf={verrouOf} stats={stats}
          onCell={(employeId, date, e) => { if (!tenterSaisie(employeId, date)) return; setMenu({ key: `${employeId}|${date}`, employeId, date, x: e.clientX, y: e.clientY }); }}
          onColonne={(date) => demanderRemplir('colonne', date)}
          onLigneTout={(employeId) => setRange(employeId, visibleDates, 'PRESENT')}
          onLigneClear={(employeId) => setRange(employeId, visibleDates, null)}
        />
      )}

      <Legende />

      {/* Menu de sélection de statut (popover) */}
      {menu && (
        <div className="fixed z-50 w-52 overflow-hidden rounded-lg border border-border bg-surface-secondary py-1 shadow-lg" style={{ left: Math.min(menu.x, window.innerWidth - 220), top: Math.min(menu.y, window.innerHeight - 360) }} onClick={(e) => e.stopPropagation()}>
          {TYPES_ORDRE.map((t) => (
            <button key={t} onClick={() => { setCell(menu.employeId, menu.date, t); setMenu(null); }} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-surface-hover">
              <span className="flex size-5 items-center justify-center rounded text-[10px] font-bold text-white" style={{ backgroundColor: TYPE_UI[t].color }}>{TYPE_UI[t].abbr}</span>
              {TYPE_UI[t].label}
            </button>
          ))}
          <button onClick={() => { setCell(menu.employeId, menu.date, null); setMenu(null); }} className="flex w-full items-center gap-2 border-t border-border px-3 py-1.5 text-left text-sm text-content-muted hover:bg-surface-hover">
            <Eraser className="size-4" /> Effacer
          </button>
        </div>
      )}

      {/* Axe 5 — confirmation du remplissage de masse (cases vides uniquement). */}
      <Modal
        open={confirmFill !== null}
        onOpenChange={(o) => { if (!o) setConfirmFill(null); }}
        title="Marquer présents (cases vides)"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmFill(null)}>Annuler</Button>
            {confirmFill && confirmFill.n > 0 && <Button onClick={executerRemplir}><CheckCheck className="size-4" /> Confirmer</Button>}
          </>
        }
      >
        {confirmFill && (confirmFill.n > 0 ? (
          <p className="text-sm text-content-secondary">
            Marquer <strong className="text-content-primary">{confirmFill.n}</strong> employé(s) non encore pointé(s) comme <strong className="text-content-primary">présents</strong> pour <strong className="text-content-primary">{confirmFill.label}</strong> ?
            <br /><br />
            <span className="text-content-muted">Cette action ne modifie pas les pointages déjà saisis (présent, absent, mission, congé…).</span>
          </p>
        ) : (
          <p className="text-sm text-content-secondary">Toutes les cases sont déjà saisies pour <strong className="text-content-primary">{confirmFill.label}</strong>. Rien à remplir.</p>
        ))}
      </Modal>
    </div>
  );
}

/* ------------------------------ Vue journalière ---------------------------- */
function VueJournaliere({ employes, date, cells, chantiers, typeOf, onSave, onClear, onMenu }: {
  employes: PointageLigneEmploye[];
  date: Date;
  cells: Map<string, PointageCellule>;
  chantiers: { id: string; code: string; nom: string }[];
  typeOf: (employeId: string, date: string) => PointageType | null;
  onSave: (p: { employe_id: string; date: string; type: PointageType; heure_arrivee?: string | null; heure_depart?: string | null; heures_sup_25?: number; chantier_id?: string | null; commentaire?: string | null }) => void;
  onClear: (employeId: string) => void;
  onMenu: (employeId: string, e: React.MouseEvent) => void;
}) {
  const d = iso(date);
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      {employes.map((e) => (
        <CarteJour key={e.employe_id} employe={e} date={d} cell={cells.get(`${e.employe_id}|${d}`)} type={typeOf(e.employe_id, d)} chantiers={chantiers} onSave={onSave} onClear={() => onClear(e.employe_id)} onMenu={(ev) => onMenu(e.employe_id, ev)} />
      ))}
    </div>
  );
}

function CarteJour({ employe, date, cell, type, chantiers, onSave, onClear, onMenu }: {
  employe: PointageLigneEmploye; date: string; cell: PointageCellule | undefined; type: PointageType | null;
  chantiers: { id: string; code: string; nom: string }[];
  onSave: (p: { employe_id: string; date: string; type: PointageType; heure_arrivee?: string | null; heure_depart?: string | null; heures_sup_25?: number; chantier_id?: string | null; commentaire?: string | null }) => void;
  onClear: () => void; onMenu: (e: React.MouseEvent) => void;
}) {
  const [arr, setArr] = useState(hm(cell?.heure_arrivee) || '08:00');
  const [dep, setDep] = useState(hm(cell?.heure_depart) || '16:00');
  const [hs, setHs] = useState(Number(cell?.heures_sup_25 ?? 0));
  const [chantier, setChantier] = useState(cell?.chantier_id ?? '');
  const [com, setCom] = useState(cell?.commentaire ?? '');
  const [editing, setEditing] = useState(false);
  const [voir, setVoir] = useState(false); // fiche détaillée (lecture seule)
  // Sync des champs avec la cellule (re-joué à chaque refetch) — NE touche pas `editing`.
  useEffect(() => {
    setArr(hm(cell?.heure_arrivee) || '08:00'); setDep(hm(cell?.heure_depart) || '16:00');
    setHs(Number(cell?.heures_sup_25 ?? 0)); setChantier(cell?.chantier_id ?? ''); setCom(cell?.commentaire ?? '');
  }, [cell, date]);
  // L'édition ne se réinitialise qu'au changement de JOUR/EMPLOYÉ (pas au refetch).
  useEffect(() => { setEditing(false); }, [date, employe.employe_id]);

  const avecHeures = type !== null && TYPES_AVEC_HEURES.includes(type);
  const avecLieu = type !== null && TYPES_AVEC_LIEU.includes(type);
  const heures = round1(heuresEntre(arr, dep));
  const chantierObj = chantier ? chantiers.find((c) => c.id === chantier) : undefined;
  const projetCode = chantierObj?.code ?? (chantier ? 'Projet' : '');
  const projetLabel = chantierObj ? `${chantierObj.code} · ${chantierObj.nom}` : (chantier ? 'Projet' : '— Sans projet —');
  const dateLongue = (() => { const d = new Date(`${date}T00:00:00`); return `${JOUR_NOM[d.getDay()]} ${d.getDate()} ${MOIS[d.getMonth()]} ${d.getFullYear()}`; })();
  // En édition si : clic « Modifier », OU mission/déplacement dont le motif n'est pas saisi.
  const enEdition = editing || (avecLieu && !com.trim());
  const quick = (t: PointageType) => {
    onSave({ employe_id: employe.employe_id, date, type: t, ...(TYPES_AVEC_HEURES.includes(t) ? { heure_arrivee: arr, heure_depart: dep } : {}) });
    setEditing(TYPES_AVEC_HEURES.includes(t)); // types à heures -> on reste en édition pour ajuster/enregistrer
  };
  const maj = () => {
    if (!type) return;
    // « Présent » = journée standard 08:00–16:00. Dès que l'utilisateur ajuste
    // les horaires, la journée bascule automatiquement en « Horaire libre »
    // (qui affiche le nombre d'heures réellement effectuées).
    const effType: PointageType = type === 'PRESENT' && (arr !== '08:00' || dep !== '16:00') ? 'HORAIRE_LIBRE' : type;
    onSave({ employe_id: employe.employe_id, date, type: effType, heure_arrivee: arr, heure_depart: dep, heures_sup_25: Number(hs) || 0, chantier_id: chantier || null, commentaire: avecLieu ? (com.trim() || null) : null });
    setEditing(false); // enregistré -> fiche récap
  };

  return (
    <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="flex items-center gap-1.5 font-semibold text-content-primary">
            {employe.prenom} {employe.nom}
            {cell?.verrouille && <span className="inline-flex items-center gap-0.5 rounded bg-surface-hover px-1.5 py-0.5 text-[10px] font-medium text-content-muted"><Lock className="size-3" /> Clôturé</span>}
          </p>
          <p className="text-xs text-content-muted">{employe.poste ?? employe.matricule}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => quick('PRESENT')} className={cn('rounded-md border px-3 py-1.5 text-sm font-medium transition-colors', type === 'PRESENT' ? 'border-transparent bg-status-done/15 text-status-done' : 'border-border text-content-secondary hover:bg-surface-hover')}>✓ Présent</button>
          <button onClick={() => quick('ABSENT_NON_JUSTIFIE')} className={cn('rounded-md border px-3 py-1.5 text-sm font-medium transition-colors', type === 'ABSENT_NON_JUSTIFIE' ? 'border-transparent bg-status-stuck/15 text-status-stuck' : 'border-border text-content-secondary hover:bg-surface-hover')}>✕ Absent</button>
          <button onClick={onMenu} className="rounded-md border border-border px-2.5 py-1.5 text-sm text-content-secondary hover:bg-surface-hover" aria-label="Autre statut"><MoreHorizontal className="size-4" /></button>
          {type && <button onClick={onClear} className="rounded-md border border-border px-2 py-1.5 text-content-muted hover:bg-surface-hover" aria-label="Effacer"><Eraser className="size-4" /></button>}
        </div>
      </div>

      {type && (
        <div className="mt-3 flex items-center gap-2">
          {/* Axes 1+2 : « Présent » discret (neutre) ; les autres statuts gardent leur couleur vive. */}
          {type === 'PRESENT' ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-hover px-2.5 py-0.5 text-xs font-medium text-content-secondary">{TYPE_UI[type].label}</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold text-white" style={{ backgroundColor: TYPE_UI[type].color }}>{TYPE_UI[type].label}</span>
          )}
        </div>
      )}

      {/* FICHE RÉCAP (lecture) — affichée une fois enregistré, hors édition. */}
      {type && avecHeures && !enEdition && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md bg-surface-primary px-3 py-2.5 text-sm">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 text-content-secondary">
            {type === 'PRESENT' && arr === '08:00' && dep === '16:00' && Number(hs) === 0 ? (
              <span className="text-content-muted">Journée standard · 08:00–16:00 · 8 h</span>
            ) : (
              <>
                <span className="tabular-nums"><strong className="text-content-primary">{hm(arr)}–{hm(dep)}</strong></span>
                <span className="font-semibold text-brand-primary">{heures} h</span>
                {Number(hs) > 0 && <span>+ {hs} h sup.</span>}
              </>
            )}
            {projetCode && <span className="truncate">· {projetCode}</span>}
            {avecLieu && com.trim() && <span className="truncate">· 📍 {com}</span>}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Button variant="ghost" size="sm" onClick={() => setVoir(true)}>Visualiser</Button>
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>Modifier</Button>
          </div>
        </div>
      )}

      {/* ÉDITION (motif mission/déplacement + horaires + H. sup. + projet). */}
      {avecHeures && enEdition && (
        <>
          {avecLieu && (
            <div className="mt-3">
              <label className="text-xs font-medium text-content-secondary">
                {type === 'MISSION' ? '📍 Motif / lieu de la mission' : '📍 Motif / destination du déplacement'}
                <input value={com} onChange={(e) => setCom(e.target.value)} maxLength={255} autoFocus
                  placeholder={type === 'MISSION' ? "Ex. Alger — réunion maître d'ouvrage" : 'Ex. Carrière Hassi Bahbah — enlèvement granulats'}
                  className="mt-1 h-9 w-full rounded-md border border-border bg-surface-secondary px-2 text-sm" />
              </label>
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-12 sm:items-end">
            <label className="text-xs text-content-secondary sm:col-span-3">{type === 'HORAIRE_LIBRE' ? 'Rentrée' : 'Arrivée'}<input type="time" value={arr} onChange={(e) => setArr(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border bg-surface-secondary px-2 text-sm" /></label>
            <label className="text-xs text-content-secondary sm:col-span-3">Départ<input type="time" value={dep} onChange={(e) => setDep(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border bg-surface-secondary px-2 text-sm" /></label>
            <div className="text-xs text-content-secondary sm:col-span-2">Heures<div className="mt-1 flex h-9 items-center font-semibold text-brand-primary">{heures} h</div></div>
            <label className="text-xs text-content-secondary sm:col-span-2">H. sup.<input type="number" step="1" min="0" value={hs} onChange={(e) => setHs(Number(e.target.value))} className="mt-1 h-9 w-full rounded-md border border-border bg-surface-secondary px-2 text-sm" /></label>
            <div className="sm:col-span-2"><Button size="sm" className="w-full" onClick={maj}>Mettre à jour</Button></div>
            <label className="text-xs text-content-secondary sm:col-span-12">Projet (chantier)
              <select value={chantier} onChange={(e) => setChantier(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border bg-surface-secondary px-2 text-sm">
                <option value="">— Sans projet —</option>
                {chantiers.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}
              </select>
            </label>
          </div>
        </>
      )}

      {/* FICHE DÉTAILLÉE (lecture seule) — toutes les infos de la journée. */}
      <Modal
        open={voir}
        onOpenChange={setVoir}
        title="Fiche de pointage"
        description={`${employe.prenom} ${employe.nom} · ${dateLongue}`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setVoir(false)}>Fermer</Button>
            {type && <Button onClick={() => { setVoir(false); setEditing(true); }}>Modifier</Button>}
          </>
        }
      >
        {type ? (
          <dl className="divide-y divide-border text-sm">
            <FicheRow label="Employé" value={`${employe.prenom} ${employe.nom}`} />
            <FicheRow label="Poste" value={employe.poste ?? employe.matricule} />
            <FicheRow label="Date" value={dateLongue} />
            <FicheRow
              label="Statut"
              value={
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-2.5 rounded-full" style={{ backgroundColor: TYPE_UI[type].color }} />
                  <span className="font-medium text-content-primary">{TYPE_UI[type].label}</span>
                </span>
              }
            />
            {avecHeures && (
              <>
                <FicheRow label={type === 'HORAIRE_LIBRE' ? 'Rentrée' : 'Arrivée'} value={hm(arr)} />
                <FicheRow label="Départ" value={hm(dep)} />
                <FicheRow label="Heures travaillées" value={<span className="font-semibold text-brand-primary">{heures} h</span>} />
                {Number(hs) > 0 && <FicheRow label="Heures supplémentaires" value={`+ ${hs} h`} />}
              </>
            )}
            <FicheRow label="Chantier affecté" value={projetLabel} />
            {avecLieu && com.trim() && (
              <FicheRow label={type === 'MISSION' ? 'Motif / lieu' : 'Motif / destination'} value={com.trim()} />
            )}
          </dl>
        ) : (
          <p className="text-sm text-content-muted">Aucun statut saisi pour cette journée.</p>
        )}
      </Modal>
    </div>
  );
}

/** Ligne « libellé / valeur » de la fiche de pointage détaillée. */
function FicheRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <dt className="shrink-0 text-content-muted">{label}</dt>
      <dd className="min-w-0 text-right text-content-primary">{value}</dd>
    </div>
  );
}

/* ------------------------------- Vue grille -------------------------------- */
function Grille({ vue, dates, employes, typeOf, heuresOf, commentOf, plageOf, verrouOf, stats, onCell, onColonne, onLigneTout, onLigneClear }: {
  vue: Vue; dates: Date[]; employes: PointageLigneEmploye[];
  typeOf: (employeId: string, date: string) => PointageType | null;
  heuresOf: (employeId: string, date: string) => number;
  commentOf: (employeId: string, date: string) => string | null;
  plageOf: (employeId: string, date: string) => string | null;
  verrouOf: (employeId: string, date: string) => boolean;
  stats: (employeId: string, dates: Date[]) => { h: number; j: number };
  onCell: (employeId: string, date: string, e: React.MouseEvent) => void;
  onColonne: (date: string) => void;
  onLigneTout: (employeId: string) => void;
  onLigneClear: (employeId: string) => void;
}) {
  const wide = vue === 'mois'; // mensuel = grille compacte ; hebdomadaire = cases larges détaillées.
  const scroller = useRef<HTMLDivElement>(null);

  // Axe 3 — résumé d'anomalies par employé sur la période (depuis les données réelles).
  const anomaliesDe = (employeId: string): { label: string; color: string }[] => {
    let abs = 0, conge = 0, maladie = 0, reduit = 0, sup = 0;
    for (const d of dates) {
      const t = typeOf(employeId, iso(d));
      if (!t) continue;
      if (t === 'ABSENT_NON_JUSTIFIE') abs += 1;
      else if (t === 'CONGE') conge += 1;
      else if (t === 'MALADIE') maladie += 1;
      if (TYPES_TRAVAIL.includes(t)) {
        const h = heuresOf(employeId, iso(d));
        if (h > 8) sup += h - 8;
        else if (h > 0 && h < 8) reduit += 8 - h;
      }
    }
    const out: { label: string; color: string }[] = [];
    if (abs) out.push({ label: `${abs} abs`, color: '#dc2626' });
    if (round1(reduit)) out.push({ label: `−${round1(reduit)} h`, color: '#b45309' });
    if (round1(sup)) out.push({ label: `+${round1(sup)} h sup`, color: '#2563eb' });
    if (conge) out.push({ label: `${conge} cong.`, color: '#2563eb' });
    if (maladie) out.push({ label: `${maladie} mal.`, color: '#9333ea' });
    return out;
  };

  return (
    <div ref={scroller} className="overflow-x-auto rounded-lg border border-border bg-surface-secondary shadow-sm">
      <table className={cn('border-collapse text-sm', wide ? 'w-full' : 'min-w-full')}>
        <thead>
          <tr className="border-b border-border bg-surface-primary/40">
            <th className={cn('sticky left-0 z-10 bg-surface-secondary px-3 py-2 text-left text-[11px] font-semibold uppercase text-content-muted', wide ? 'min-w-[190px]' : 'w-[200px]')}>Employé ({employes.length})</th>
            {dates.map((d) => (
              <th key={iso(d)} className={cn('py-1.5 text-center font-semibold', wide ? 'w-8 px-0 text-[11px]' : 'min-w-[92px] px-1 text-xs', estRepos(d) ? 'bg-status-stuck/10 text-status-stuck' : 'text-content-secondary')}>
                {wide ? (
                  <>
                    <div>{JOUR_LETTRE[d.getDay()]}{estRepos(d) ? ' 🌙' : ''}</div>
                    <div className="text-content-muted">{d.getDate()}</div>
                  </>
                ) : (
                  <>
                    <div className="text-[13px]">{JOUR_ABBR[d.getDay()]}{estRepos(d) ? ' · repos' : ''}</div>
                    <div className="text-[11px] font-normal text-content-muted">{pad(d.getDate())}/{pad(d.getMonth() + 1)}</div>
                  </>
                )}
                {!estRepos(d) && <button onClick={() => onColonne(iso(d))} title="Tous présents ce jour" className="mx-auto mt-0.5 block rounded p-0.5 text-content-muted hover:bg-brand-primary/10 hover:text-brand-primary"><CheckCheck className="size-3.5" /></button>}
              </th>
            ))}
            <th className="bg-status-done/10 px-2 py-2 text-center text-[11px] font-semibold uppercase text-status-done">Total&nbsp;H</th>
            <th className="bg-brand-primary/10 px-2 py-2 text-center text-[11px] font-semibold uppercase text-brand-primary">Jours</th>
            <th className="min-w-[128px] whitespace-nowrap px-2 py-2 text-left text-[11px] font-semibold uppercase text-content-muted">Anomalies</th>
          </tr>
        </thead>
        <tbody>
          {employes.map((e) => {
            const s = stats(e.employe_id, dates);
            return (
              <tr key={e.employe_id} className="border-b border-border last:border-0">
                <td className="sticky left-0 z-10 bg-surface-secondary px-3 py-1.5">
                  <div className="font-medium text-content-primary">{e.prenom} {e.nom}</div>
                  <div className="flex items-center gap-2">
                    <span className="truncate text-[11px] text-content-muted">{e.poste ?? e.matricule}</span>
                    <button onClick={() => onLigneTout(e.employe_id)} className="rounded px-1 text-[10px] font-semibold text-status-done hover:bg-status-done/10">✓ tout</button>
                    <button onClick={() => onLigneClear(e.employe_id)} className="rounded px-1 text-[10px] font-semibold text-content-muted hover:bg-surface-hover">effacer</button>
                  </div>
                </td>
                {dates.map((d) => {
                  const key = iso(d); const t = typeOf(e.employe_id, key); const ui = t ? TYPE_UI[t] : null;
                  const h = t ? heuresOf(e.employe_id, key) : 0;
                  const com = t ? commentOf(e.employe_id, key) : null;
                  const plage = t ? plageOf(e.employe_id, key) : null;
                  const avecH = t ? TYPES_AVEC_HEURES.includes(t) : false;
                  // Axes 1+2 : la présence STANDARD (8 h, sans note) est discrète ;
                  // seules les exceptions ressortent en couleur vive.
                  const standard = t === 'PRESENT' && round1(h) === 8 && !com;
                  const verrou = verrouOf(e.employe_id, key); // Axe 7 — cellule clôturée
                  return (
                    <td key={key} className={cn('relative', wide ? 'p-0.5 align-middle' : 'p-1 align-top', 'text-center', !ui && estRepos(d) && 'bg-status-stuck/5')}>
                      <button onClick={(ev) => onCell(e.employe_id, key, ev)}
                        title={ui ? `${ui.label}${plage ? ' · ' + plage : ''}${h ? ' · ' + round1(h) + ' h' : ''}${com ? ' · 📍 ' + com : ''}` : 'Vide — cliquer pour saisir'}
                        className={cn('mx-auto flex items-center justify-center rounded transition-colors',
                          wide ? 'size-7 text-xs font-bold' : 'min-h-[54px] w-full flex-col gap-0.5 px-1 py-1.5 text-[11px]',
                          standard
                            ? 'border border-border/60 bg-surface-hover/50 text-content-muted'
                            : ui
                              ? 'font-semibold text-white'
                              : cn('text-content-muted hover:bg-surface-hover', !wide && 'border border-dashed border-border/70'))}
                        style={ui && !standard ? { backgroundColor: ui.color } : undefined}>
                        {wide
                          ? (standard ? 'P' : ui ? ui.abbr : '·')
                          : standard
                            ? <span className="font-medium opacity-70">Présent</span>
                            : ui
                              ? (
                                <>
                                  <span className="font-semibold leading-tight">{ui.label}</span>
                                  {avecH && h ? <span className="text-[10px] font-medium opacity-90">{round1(h)} h</span> : null}
                                  {com ? <span className="max-w-full truncate text-[9px] font-normal opacity-95">📍 {com}</span> : null}
                                </>
                              )
                              : <span className="text-base text-content-muted">+</span>}
                      </button>
                      {verrou && <Lock className="pointer-events-none absolute right-1 top-1 size-2.5 text-content-muted opacity-80" />}
                    </td>
                  );
                })}
                <td className="bg-status-done/5 px-2 text-center font-semibold tabular-nums text-status-done">{s.h ? `${s.h}h` : ''}</td>
                <td className="bg-brand-primary/5 px-2 text-center font-semibold tabular-nums text-brand-primary">{s.j ? `${s.j}j` : ''}</td>
                <td className="min-w-[128px] px-2 py-1">
                  {(() => {
                    const anos = anomaliesDe(e.employe_id);
                    return anos.length === 0 ? <span className="text-content-muted">—</span> : (
                      <div className="flex flex-wrap gap-1">
                        {anos.map((a, i) => <span key={i} style={{ color: a.color, backgroundColor: `${a.color}1a` }} className="whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold">{a.label}</span>)}
                      </div>
                    );
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* -------------------------------- Légende ---------------------------------- */
function Legende() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2 rounded-lg border border-border bg-surface-secondary p-3 text-xs shadow-sm">
      <span className="font-semibold text-content-muted">Légende :</span>
      {TYPES_ORDRE.map((t) => (
        <span key={t} className="flex items-center gap-1.5">
          <span className="flex size-4 items-center justify-center rounded text-[9px] font-bold text-white" style={{ backgroundColor: TYPE_UI[t].color }}>{TYPE_UI[t].abbr}</span>
          {TYPE_UI[t].label}
        </span>
      ))}
      <span className="flex items-center gap-1.5 text-content-muted"><CalendarCheck className="size-3.5" /> 🌙 vendredi = repos</span>
    </div>
  );
}
